<template>
  <div class="sales-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <div class="hero-section">
      <div class="hero-background">
        <img :src="heroBackgroundImage" alt="Sales Background" />
        <div class="slide-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">销售网络</h1>
        <p class="page-subtitle">全球布局，服务世界各地客户</p>
      </div>
    </div>
    
    <div class="content-section">
      <div class="container">
        <div class="sales-intro">
          <h2 class="section-title">全球销售网络</h2>
          <p class="section-description">
            我们的产品销往全球多个国家和地区，建立了完善的销售和服务网络，
            为客户提供及时、专业的技术支持和服务。
          </p>
        </div>
        
        <div class="world-map">
          <div class="map-placeholder">
            <img src="/src/assets/images/map.jpg" alt="World Map" class="map-icon" />
            <p class="map-text">全球销售网络覆盖图</p>
          </div>
        </div>
        
        <div class="regions-grid">
          <div
            class="region-card"
            v-for="region in regions"
            :key="region.id"
            :style="{ backgroundImage: `url(${region.backgroundImage})` }"
          >
            <div class="card-overlay"></div>
            <div class="card-content">
              <div class="region-icon">
                <img :src="region.icon" :alt="region.name" />
              </div>
              <h3 class="region-name">{{ region.name }}</h3>
              <div class="region-stats">
                <div class="stat-item">
                  <span class="stat-number">{{ region.countries }}</span>
                  <span class="stat-label">覆盖国家</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ region.partners }}</span>
                  <span class="stat-label">合作伙伴</span>
                </div>
                <div class="stat-item">
                  <span class="stat-number">{{ region.offices }}</span>
                  <span class="stat-label">办事处</span>
                </div>
              </div>
              <ul class="region-features">
                <li v-for="feature in region.features" :key="feature" class="feature-item">
                  {{ feature }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="contact-section">
          <h2 class="section-title">联系我们</h2>
          <div class="contact-grid">
            <div class="contact-card" v-for="contact in contactInfo" :key="contact.type">
              <div class="contact-icon">
                <img :src="contact.icon" :alt="contact.type" />
              </div>
              <h4 class="contact-type">{{ contact.type }}</h4>
              <p class="contact-info">{{ contact.info }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const heroBackgroundImage = new URL('@/assets/carousel/slide1.webp', import.meta.url).href

interface Region {
  id: number
  name: string
  icon: string
  countries: number
  partners: number
  offices: number
  features: string[]
  backgroundImage: string
}

interface ContactInfo {
  type: string
  info: string
  icon: string
}

const regions = ref<Region[]>([
  {
    id: 1,
    name: "亚太地区",
    icon: "/src/assets/assets/svg/globalization.svg",
    countries: 15,
    partners: 120,
    offices: 8,
    features: ["中国总部", "日本分公司", "新加坡区域中心", "印度办事处"],
    backgroundImage: new URL('@/assets/products-center/Customerized Solutions.png', import.meta.url).href
  },
  {
    id: 2,
    name: "欧洲地区",
    icon: "/src/assets/assets/svg/airplane.svg",
    countries: 12,
    partners: 85,
    offices: 6,
    features: ["德国分公司", "法国办事处", "英国销售中心", "意大利技术支持"],
    backgroundImage: new URL('@/assets/products-center/E-Motion products.png', import.meta.url).href
  },
  {
    id: 3,
    name: "北美地区",
    icon: "/src/assets/assets/svg/laptop.svg",
    countries: 3,
    partners: 65,
    offices: 4,
    features: ["美国总部", "加拿大分公司", "墨西哥办事处", "技术服务中心"],
    backgroundImage: new URL('@/assets/products-center/Machine Parts.png', import.meta.url).href
  },
  {
    id: 4,
    name: "南美地区",
    icon: "/src/assets/assets/svg/shop.svg",
    countries: 8,
    partners: 45,
    offices: 3,
    features: ["巴西分公司", "阿根廷办事处", "智利销售中心", "技术支持站"],
    backgroundImage: new URL('@/assets/products-center/Pneumatic Controllers.png', import.meta.url).href
  }
])

const contactInfo = ref<ContactInfo[]>([
  {
    type: "全球销售热线",
    info: "+86 400-123-4567",
    icon: "/src/assets/assets/svg/service.svg"
  },
  {
    type: "商务合作邮箱",
    info: "business@example.com",
    icon: "/src/assets/assets/svg/user_avatar.svg"
  },
  {
    type: "技术支持",
    info: "support@example.com",
    icon: "/src/assets/assets/svg/system.svg"
  },
  {
    type: "总部地址",
    info: "中国上海市浦东新区科技园区",
    icon: "/src/assets/assets/svg/hot.svg"
  }
])
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.sales-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 20px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.content-section {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.sales-intro {
  text-align: center;
  margin-bottom: 60px;
}

.section-title {
  font-size: 36px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Bitter', serif;
}

.section-description {
  font-size: 18px;
  color: #7f8c8d;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Hind', sans-serif;
}

.world-map {
  text-align: center;
  margin-bottom: 80px;
}

.map-placeholder {
  background: white;
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
}

.map-icon {
  width: 120px;
  height: 120px;
  margin-bottom: 30px;
  opacity: 0.8;
}

.map-text {
  font-size: 18px;
  color: #7f8c8d;
  font-weight: 500;
}

.regions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 80px;
}

.region-card {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.region-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.75));
  transition: all 0.4s ease;
}

.region-card:hover .card-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85));
}

.card-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 35px;
  text-align: center;
}

.region-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
  background: rgba(217, 127, 62, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.95;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.region-card:hover .region-icon {
  transform: scale(1.15) translateY(-5px);
}

.region-icon img {
  width: 40px;
  height: 40px;
  filter: brightness(0) invert(1);
}

.region-name {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.4;
  font-family: 'Bitter', serif;
  letter-spacing: 0.2px;
  margin-bottom: 20px;
  transition: transform 0.3s;
}

.region-card:hover .region-name {
  transform: translateY(-3px);
}

.region-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 25px;
}

.stat-item {
  text-align: center;
  padding: 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-family: 'Hind', sans-serif;
}

.region-features {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-item {
  padding: 6px 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  position: relative;
  padding-left: 20px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  font-family: 'Hind', sans-serif;
}

.feature-item:before {
  content: "•";
  color: #d97f3e;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.contact-section {
  margin-top: 60px;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.contact-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
}

.contact-card:hover {
  transform: translateY(-5px);
}

.contact-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #2196F3 0%, #0D47A1 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contact-icon img {
  width: 30px;
  height: 30px;
  filter: brightness(0) invert(1);
}

.contact-type {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 10px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', sans-serif;
}

.contact-info {
  font-size: 16px;
  color: #7f8c8d;
  line-height: 1.5;
}

@media (max-width: 1024px) {
  .regions-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .content-section {
    padding: 70px 0;
  }

  .regions-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .region-card {
    height: 300px;
  }

  .page-title {
    font-size: 38px;
  }

  .page-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .section-title {
    font-size: 28px;
  }
  
  .section-description {
    font-size: 16px;
  }
  
  .region-stats {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .contact-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}
</style>